import React, { useState, useMemo, useEffect, useReducer } from 'react';
import {
  Crown, ShoppingBag, BookOpen, Calendar, Dices,
  Send, CheckCircle, Eye, Clock, Users, X, ChevronDown, ChevronUp
} from 'lucide-react';
import { systemBus, EVENTS } from '../services/systemBus';
import { useUI } from '../contexts/UIContext';
// [PHASE B / #009 — B5-2]: Convert the numeric game-day prop to a
//   FaerunianDate object before rendering. Pre-fix the JSX read
//   `currentGameDay.year` / `.day` / `.monthName` directly off a
//   number, so the party-view calendar header showed `Year ` (empty)
//   and the date title rendered as just the fallback text.
import { FaerunianDate } from '../data/faerunianCalendar';
// [PHASE B / #015 — B4-4/B4-5]: Merge the 3 historical roll-table
//   stores so any user-authored table is reachable from the DM's
//   Loot Push surface.
import { mergeRollTables } from '../utils/rollTableMerge';
// [PHASE B / #066 — 2026-05-07]: B4-2 stopgap — actually grant rolled
//   loot to the DM's local inventory after a successful push. Pre-#066
//   the rolled item was a display string only; nothing materialized.
//   grantRollResult translates the structured `{kind, ref, qty}` entry
//   into a mutation that App.jsx applies via onLootGranted (wired to
//   InventoryContext.addItem). Legacy string-only entries yield null
//   and skip the grant — back-compat preserved.
import { grantRollResult } from '../utils/grantRollResult';

/**
 * DMToolsPanel - DM Content Distribution System
 *
 * Allows DMs to push content to party members:
 * - Shops (players can browse and purchase)
 * - Journal entries (lore, notes, quest info)
 * - Calendar events (game world dates, reminders)
 * - Loot rolls (roll tables for treasure distribution)
 *
 * [PURPOSE]: Enable DMs to share campaign content with players in real-time
 * [WHY]: Core D&D gameplay requires DM-to-player content distribution
 * [INTEGRATION]: Uses useEnhancedPartySync for Firebase push methods
 */
const DMToolsPanel = ({
  isDM = false,
  partyMembers = {}, // From useEnhancedPartySync (not in context yet, defer to future phase)
  pushedContent = {}, // From useEnhancedPartySync (not in context yet, defer to future phase)
  // [PHASE 4B - PARTIAL MIGRATION]: Keeping function props for Phase 4B Step 6
  // Push methods from useEnhancedPartySync
  pushShopToParty = null,
  pushNoteToParty = null,
  pushCalendarEventToParty = null,
  pushLootRollToParty = null,
  // [PHASE B / #066 — 2026-05-07]: Optional callback fired with the
  //   grantRollResult mutation after a successful loot push, so the
  //   DM's local inventory deposits the rolled item(s). App.jsx wires
  //   this to InventoryContext.addItem; tests can stub directly.
  //   Null = display-only (pre-#066 behavior).
  onLootGranted = null,
  // Data sources (not in contexts yet, defer to future phase)
  activeShops = [],
  journalEntries = [],
  currentGameDay = null,
  // [PHASE B / #015 — B4-4/B4-5]: Three disconnected stores held roll
  //   tables historically — `customRollTables` (CustomRollTableDialog
  //   + Advanced Shop builder), `rollTables` (RollTablesTab editor),
  //   `journalRollTables` (legacy journal-side store). Pre-fix this
  //   panel only consumed `customRollTables`, so a user authoring a
  //   loot table in the Roll Tables tab saw "Create Roll Table" here
  //   instead of being able to push their table to the party.
  customRollTables = [],
  rollTables = [],
  journalRollTables = [],
  // UI callbacks
  onOpenShop = () => {},
  onOpenJournal = () => {},
  onOpenCalendar = () => {},
  onOpenRollTables = () => {}
}) => {
  // [PHASE 4B - MIGRATED]: Now using context hooks instead of props
  const { showStatus } = useUI();

  const [activeSection, setActiveSection] = useState('shops'); // shops, notes, calendar, loot
  const [showHistory, setShowHistory] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);

  // [PHASE 5 SESSION 2]: Force re-render mechanism for reactive updates
  // [PURPOSE]: React to party member and HP changes in real-time
  // [WHY]: DM needs to see party updates immediately for coordinating the game
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [PHASE 5 SESSION 2]: Event subscriptions for reactive DM tools updates
  // [PURPOSE]: Subscribe to party member and HP change events
  // [WHY]: DM tools should show real-time party status and member updates
  // [INTEGRATION]: Monitors party composition and member health changes
  useEffect(() => {
    // [HANDLER]: Central event handler for DM-relevant events
    const handleDMEvent = (eventName, data) => {
      // Force re-render to show updated party member list and status
      forceUpdate();
    };

    // [PARTY_MEMBER_ADDED]: New party member joined
    const unsubscribeMemberAdded = systemBus.on(EVENTS.PARTY_MEMBER_ADDED, (data) =>
      handleDMEvent('PARTY_MEMBER_ADDED', data));

    // [PARTY_MEMBER_UPDATED]: Party member data changed
    const unsubscribeMemberUpdated = systemBus.on(EVENTS.PARTY_MEMBER_UPDATED, (data) =>
      handleDMEvent('PARTY_MEMBER_UPDATED', data));

    // [HP_CHANGED]: Party member HP changed (important for DM monitoring)
    const unsubscribeHPChanged = systemBus.on(EVENTS.HP_CHANGED, (data) =>
      handleDMEvent('HP_CHANGED', data));

    // Cleanup: Unsubscribe on component unmount
    return () => {
      unsubscribeMemberAdded();
      unsubscribeMemberUpdated();
      unsubscribeHPChanged();
    };
  }, []); // Empty dependency array = subscribe once on mount

  // [PSR-020 FIX — MAY 2026]: defensive null-guard on partyMembers — the prop
  //   default is {} but Firebase listener may briefly emit null during the
  //   first fire. PartyPanel guards with `partyMembers || {}`; matching here.
  const safeMembers = partyMembers || {};
  const memberCount = Object.keys(safeMembers).length;

  // Count content by type
  const contentCounts = useMemo(() => {
    const counts = {
      shops: Object.keys(pushedContent?.shops || {}).length,
      notes: Object.keys(pushedContent?.notes || {}).length,
      calendar: Object.keys(pushedContent?.calendarEvents || {}).length,
      loot: Object.keys(pushedContent?.lootRolls || {}).length
    };
    counts.total = Object.values(counts).reduce((sum, c) => sum + c, 0);
    return counts;
  }, [pushedContent]);

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  // Get view count for pushed content
  const getViewCount = (viewedBy = {}) => {
    return Object.keys(viewedBy).length;
  };

  if (!isDM) {
    return (
      <div className="bg-gray-800 p-6 rounded-lg text-center">
        <Crown size={48} className="mx-auto text-gray-600 mb-4" />
        <h3 className="text-xl font-bold text-gray-400 mb-2">DM Tools</h3>
        <p className="text-gray-500">
          Only the Dungeon Master can access these tools.
        </p>
        <p className="text-sm text-gray-600 mt-2">
          The party creator is automatically designated as DM.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 p-6 rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center text-purple-300">
            <Crown className="mr-3" size={32} />
            DM Tools
          </h2>
          <p className="text-gray-400 mt-1">
            Push content to your party • {memberCount} member{memberCount !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="bg-purple-700 hover:bg-purple-600 px-4 py-2 rounded flex items-center gap-2 transition-colors"
        >
          <Clock size={16} />
          {showHistory ? 'Hide' : 'View'} History
          {contentCounts.total > 0 && (
            <span className="bg-purple-900 px-2 py-0.5 rounded-full text-xs">
              {contentCounts.total}
            </span>
          )}
        </button>
      </div>

      {!showHistory ? (
        <>
          {/* Content Type Selector */}
          <div className="grid grid-cols-4 gap-2 mb-6">
            <button
              onClick={() => setActiveSection('shops')}
              className={`p-4 rounded-lg transition-all ${
                activeSection === 'shops'
                  ? 'bg-purple-600 border-2 border-purple-400 shadow-lg'
                  : 'bg-gray-700 border-2 border-gray-600 hover:bg-gray-600'
              }`}
            >
              <ShoppingBag size={24} className="mx-auto mb-2" />
              <div className="text-sm font-bold">Shops</div>
              <div className="text-xs text-gray-400">{activeShops.length} available</div>
            </button>

            <button
              onClick={() => setActiveSection('notes')}
              className={`p-4 rounded-lg transition-all ${
                activeSection === 'notes'
                  ? 'bg-purple-600 border-2 border-purple-400 shadow-lg'
                  : 'bg-gray-700 border-2 border-gray-600 hover:bg-gray-600'
              }`}
            >
              <BookOpen size={24} className="mx-auto mb-2" />
              <div className="text-sm font-bold">Notes</div>
              <div className="text-xs text-gray-400">{journalEntries.length} entries</div>
            </button>

            <button
              onClick={() => setActiveSection('calendar')}
              className={`p-4 rounded-lg transition-all ${
                activeSection === 'calendar'
                  ? 'bg-purple-600 border-2 border-purple-400 shadow-lg'
                  : 'bg-gray-700 border-2 border-gray-600 hover:bg-gray-600'
              }`}
            >
              <Calendar size={24} className="mx-auto mb-2" />
              <div className="text-sm font-bold">Calendar</div>
              <div className="text-xs text-gray-400">Sync events</div>
            </button>

            <button
              onClick={() => setActiveSection('loot')}
              className={`p-4 rounded-lg transition-all ${
                activeSection === 'loot'
                  ? 'bg-purple-600 border-2 border-purple-400 shadow-lg'
                  : 'bg-gray-700 border-2 border-gray-600 hover:bg-gray-600'
              }`}
            >
              <Dices size={24} className="mx-auto mb-2" />
              <div className="text-sm font-bold">Loot</div>
              <div className="text-xs text-gray-400">{customRollTables.length} tables</div>
            </button>
          </div>

          {/* Content Section */}
          <div className="bg-gray-700 p-6 rounded-lg min-h-[400px]">
            {/* SHOPS SECTION */}
            {activeSection === 'shops' && (
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <ShoppingBag className="mr-2" size={24} />
                  Push Shop to Party
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Select a shop to share with your party. Players will receive it instantly and can browse/purchase items.
                </p>

                {activeShops.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingBag size={48} className="mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-500 mb-4">No active shops</p>
                    <button
                      onClick={onOpenShop}
                      className="bg-purple-600 hover:bg-purple-500 px-4 py-2 rounded"
                    >
                      Create Shop
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {activeShops.map((shop, idx) => (
                      <div
                        key={shop.id || idx}
                        className={`bg-gray-800 p-4 rounded border-2 transition-all cursor-pointer ${
                          selectedShop === shop.id
                            ? 'border-purple-500 shadow-lg'
                            : 'border-gray-700 hover:border-purple-700'
                        }`}
                        onClick={() => setSelectedShop(shop.id)}
                      >
                        <h4 className="font-bold text-lg mb-1">{shop.shopName || `Shop ${idx + 1}`}</h4>
                        <p className="text-sm text-gray-400 mb-2">
                          🏪 {shop.shopkeeper || 'Shopkeeper'} • {shop.items?.length || 0} items
                        </p>
                        {selectedShop === shop.id && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!pushShopToParty) return;
                              // [PSR-014 FIX — MAY 2026]: Await + check return.
                              //   Pre-fix this was fire-and-forget; if Firebase
                              //   rules denied (e.g., not actually DM), the
                              //   success toast still showed. Now we await the
                              //   Promise<bool> and surface failure.
                              const ok = await pushShopToParty(shop);
                              if (ok) {
                                showStatus(`✅ Pushed "${shop.shopName}" to party!`);
                                setSelectedShop(null);
                              } else {
                                showStatus(`❌ Failed to push shop — check DM permissions or network`, 4000);
                              }
                            }}
                            className="w-full bg-purple-600 hover:bg-purple-500 px-4 py-2 rounded mt-2 flex items-center justify-center gap-2 font-bold"
                          >
                            <Send size={16} />
                            Push to Party
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* NOTES SECTION */}
            {activeSection === 'notes' && (
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <BookOpen className="mr-2" size={24} />
                  Push Journal Entry to Party
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Share lore, quest information, or campaign notes with your party.
                </p>

                {journalEntries.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen size={48} className="mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-500 mb-4">No journal entries</p>
                    <button
                      onClick={onOpenJournal}
                      className="bg-purple-600 hover:bg-purple-500 px-4 py-2 rounded"
                    >
                      Create Journal Entry
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {journalEntries.map((entry) => (
                      <div
                        key={entry.id}
                        className={`bg-gray-800 p-4 rounded border-2 transition-all cursor-pointer ${
                          selectedNote === entry.id
                            ? 'border-purple-500 shadow-lg'
                            : 'border-gray-700 hover:border-purple-700'
                        }`}
                        onClick={() => setSelectedNote(entry.id)}
                      >
                        <h4 className="font-bold text-lg mb-1">{entry.title}</h4>
                        <p className="text-xs text-gray-400 mb-2">
                          {entry.category && `📁 ${entry.category}`}
                          {entry.tags && entry.tags.length > 0 && ` • 🏷️ ${entry.tags.join(', ')}`}
                        </p>
                        <p className="text-sm text-gray-300 line-clamp-2 mb-2">{entry.content}</p>
                        {selectedNote === entry.id && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!pushNoteToParty) return;
                              // [PSR-014 FIX — MAY 2026]: Await + check return.
                              const ok = await pushNoteToParty(entry);
                              if (ok) {
                                showStatus(`✅ Pushed "${entry.title}" to party!`);
                                setSelectedNote(null);
                              } else {
                                showStatus(`❌ Failed to push note — check DM permissions or network`, 4000);
                              }
                            }}
                            className="w-full bg-purple-600 hover:bg-purple-500 px-4 py-2 rounded mt-2 flex items-center justify-center gap-2 font-bold"
                          >
                            <Send size={16} />
                            Push to Party
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* CALENDAR SECTION */}
            {activeSection === 'calendar' && (
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Calendar className="mr-2" size={24} />
                  Sync Calendar to Party
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Share the current game date with your party. All players will see the same calendar.
                </p>

                <div className="bg-gray-800 p-6 rounded border-2 border-purple-700">
                  {/* [PHASE B / #009 — B5-2]: Resolve the calendar date
                      from `currentGameDay`. App.jsx passes a numeric
                      game-day count; pre-fix the JSX read .year / .day /
                      .monthName off the number directly, so the party
                      view's calendar header showed `Year ""`. */}
                  {(() => {
                    const calendarDate = (typeof currentGameDay === 'number' && currentGameDay > 0)
                      ? FaerunianDate.fromGameDay(currentGameDay)
                      : (currentGameDay && typeof currentGameDay === 'object')
                        ? currentGameDay
                        : null;
                    // FaerunianDate exposes the month name via
                    // calendarDate.month.name (the FAERUNIAN_MONTHS row);
                    // older inline call sites passed a shape with
                    // `.monthName` directly. Fall back through both so
                    // either shape renders.
                    const dayLabel = calendarDate ? (calendarDate.day || 1) : 1;
                    const monthLabel = calendarDate
                      ? (calendarDate.monthName || calendarDate.month?.name || 'Hammer')
                      : 'Hammer';
                    const yearLabel = calendarDate ? (calendarDate.year || 1492) : 1492;
                    return (
                      <div className="text-center mb-6">
                        <div className="text-4xl font-bold text-purple-300 mb-2">
                          {dayLabel} {monthLabel}
                        </div>
                        <div className="text-xl text-gray-400">
                          Year {yearLabel}
                        </div>
                        {calendarDate?.holidayName && (
                          <div className="mt-3 text-purple-400 font-bold">
                            ✨ {calendarDate.holidayName}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <button
                    onClick={async () => {
                      // [PHASE B / #009 — B5-2]: Push the resolved
                      //   FaerunianDate, not the raw number prop, so
                      //   subscribers get a self-describing payload.
                      // [PSR-014 FIX — MAY 2026]: await + verify.
                      if (!pushCalendarEventToParty || !currentGameDay) return;
                      const payload = (typeof currentGameDay === 'number' && currentGameDay > 0)
                        ? FaerunianDate.fromGameDay(currentGameDay)
                        : currentGameDay;
                      const ok = await pushCalendarEventToParty(payload);
                      if (ok) {
                        showStatus(`✅ Synced calendar to party!`);
                      } else {
                        showStatus(`❌ Failed to sync calendar — check DM permissions or network`, 4000);
                      }
                    }}
                    className="w-full bg-purple-600 hover:bg-purple-500 px-4 py-3 rounded font-bold flex items-center justify-center gap-2"
                  >
                    <Send size={20} />
                    Sync Current Date to Party
                  </button>

                  <button
                    onClick={onOpenCalendar}
                    className="w-full mt-2 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded text-sm"
                  >
                    Open Calendar to Change Date
                  </button>
                </div>
              </div>
            )}

            {/* LOOT SECTION */}
            {activeSection === 'loot' && (
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Dices className="mr-2" size={24} />
                  Push Loot Roll to Party
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Roll a treasure table and distribute results to party members.
                </p>

                {/* [PHASE B / #015 — B4-4/B4-5]: Merge the 3 historical
                    roll-table stores so anything the user authored —
                    Roll Tables tab, Custom Roll Table dialog, or legacy
                    journal-side store — is selectable here. */}
                {(() => {
                  const allTables = mergeRollTables(customRollTables, rollTables, journalRollTables);
                  if (allTables.length === 0) {
                    return (
                      <div className="text-center py-12">
                        <Dices size={48} className="mx-auto text-gray-600 mb-4" />
                        <p className="text-gray-500 mb-4">No roll tables available</p>
                        <button
                          onClick={onOpenRollTables}
                          className="bg-purple-600 hover:bg-purple-500 px-4 py-2 rounded"
                        >
                          Create Roll Table
                        </button>
                      </div>
                    );
                  }
                  return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {allTables.map((table) => (
                      <div
                        key={table.id}
                        className={`bg-gray-800 p-4 rounded border-2 transition-all cursor-pointer ${
                          selectedTable === table.id
                            ? 'border-purple-500 shadow-lg'
                            : 'border-gray-700 hover:border-purple-700'
                        }`}
                        onClick={() => setSelectedTable(table.id)}
                      >
                        <h4 className="font-bold text-lg mb-1">{table.name}</h4>
                        <p className="text-sm text-gray-400 mb-2">
                          🎲 {table.entries?.length || 0} entries • {table.category || 'Loot'}
                        </p>
                        {selectedTable === table.id && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              // [FIX APR 2026 Phase 3D.8]: Precondition checks BEFORE rolling.
                              // Previously the push could silently fail (missing DM status, no
                              // connection, no party) and the UI re-rendered back to "select a
                              // table", looking like it wanted the user to roll AGAIN. Now
                              // explicit errors surface so user knows what went wrong.
                              if (!pushLootRollToParty) {
                                showStatus('❌ Party sync not available', 3000);
                                return;
                              }
                              if (!table.entries || table.entries.length === 0) {
                                showStatus('❌ Table has no entries', 3000);
                                return;
                              }
                              const roll = Math.floor(Math.random() * table.entries.length) + 1;
                              const result = table.entries[roll - 1] || { result: 'Unknown' };
                              try {
                                const ok = await pushLootRollToParty({
                                  table: table.name,
                                  roll,
                                  result,
                                  timestamp: new Date().toISOString()
                                });
                                if (ok === false) {
                                  // The hook returns false when user isn't DM or connection missing.
                                  showStatus('❌ Push failed — are you the DM and connected?', 4000);
                                  return;
                                }
                                // [PHASE B / #066 — 2026-05-07]: B4-2 stopgap.
                                //   Translate the rolled entry into a grant mutation
                                //   and forward to onLootGranted so it actually
                                //   deposits in the DM's local inventory. Structured
                                //   entries `{kind, ref, qty}` resolve; legacy string
                                //   entries return null and skip silently.
                                const mutation = grantRollResult(result);
                                if (mutation && typeof onLootGranted === 'function') {
                                  try {
                                    onLootGranted(mutation, result);
                                  } catch (grantErr) {
                                    console.error('[LootPush] onLootGranted threw:', grantErr);
                                    // Don't fail the whole flow — push succeeded.
                                  }
                                }
                                const grantedLabel = mutation?.activityLog
                                  || result?.result
                                  || result?.name
                                  || `roll ${roll}`;
                                showStatus(`✅ Pushed ${table.name}: ${grantedLabel}`, 3000);
                                setSelectedTable(null);
                              } catch (err) {
                                console.error('[LootPush] failed:', err);
                                showStatus(`❌ Push failed: ${err.message || 'unknown error'}`, 5000);
                              }
                            }}
                            className="w-full bg-purple-600 hover:bg-purple-500 px-4 py-2 rounded mt-2 flex items-center justify-center gap-2 font-bold"
                          >
                            <Dices size={16} />
                            Roll & Push to Party
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  );
                })()}
              </div>
            )}
          </div>
        </>
      ) : (
        /* HISTORY VIEW */
        <ContentHistory
          pushedContent={pushedContent}
          partyMembers={partyMembers}
          formatTime={formatTime}
          getViewCount={getViewCount}
        />
      )}
    </div>
  );
};

// Content History Component
// [2026-06-14]: exported for unit testing (the "Viewed by" partyMembers bugfix).
export const ContentHistory = ({ pushedContent, partyMembers, formatTime, getViewCount }) => {
  const [expandedType, setExpandedType] = useState('shops');

  const contentTypes = [
    { key: 'shops', label: 'Shops', icon: ShoppingBag, data: pushedContent?.shops || {} },
    { key: 'notes', label: 'Journal Entries', icon: BookOpen, data: pushedContent?.notes || {} },
    { key: 'calendarEvents', label: 'Calendar Events', icon: Calendar, data: pushedContent?.calendarEvents || {} },
    { key: 'lootRolls', label: 'Loot Rolls', icon: Dices, data: pushedContent?.lootRolls || {} }
  ];

  return (
    <div>
      <h3 className="text-xl font-bold mb-4">Pushed Content History</h3>
      <div className="space-y-4">
        {contentTypes.map(({ key, label, icon: Icon, data }) => {
          const items = Object.entries(data);
          const isExpanded = expandedType === key;

          return (
            <div key={key} className="bg-gray-700 rounded-lg">
              <button
                onClick={() => setExpandedType(isExpanded ? null : key)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-600 transition-colors rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Icon size={24} className="text-purple-400" />
                  <span className="font-bold">{label}</span>
                  <span className="text-sm text-gray-400">({items.length})</span>
                </div>
                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>

              {isExpanded && items.length > 0 && (
                <div className="p-4 pt-0 space-y-2">
                  {items.map(([id, content]) => (
                    <div key={id} className="bg-gray-800 p-3 rounded">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-bold">
                            {content.shop?.shopName || content.note?.title || content.event?.monthName || content.roll?.table || 'Unknown'}
                          </h4>
                          <p className="text-xs text-gray-400">
                            Pushed {formatTime(content.pushedAt)} by {content.pushedByName}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <Eye size={14} />
                          <span>{getViewCount(content.viewedBy || content.acceptedBy)}/{Object.keys(partyMembers).length}</span>
                        </div>
                      </div>
                      {content.viewedBy && Object.keys(content.viewedBy).length > 0 && (
                        <div className="text-xs text-green-400 flex items-center gap-1">
                          <CheckCircle size={12} />
                          Viewed by: {Object.entries(content.viewedBy).map(([id, time]) =>
                            // [BUGFIX 2026-06-14]: was `safeMembers[id]` — safeMembers is a
                            //   parent-scope local (line ~124), NOT a prop of ContentHistory.
                            //   ReferenceError when "Viewed by" rendered. Use the partyMembers
                            //   prop (same one line 659 uses correctly). DEV-OVERLAY-ESLINT no-undef.
                            partyMembers[id]?.characterName || 'Unknown'
                          ).join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DMToolsPanel;
